/**
 * Aggregates a flush window of RPC metrics and forwards a compact usage summary
 * through the operational-event logger facade.
 *
 * Each window is collapsed into one `rpc_usage` event per
 * (chain, method, status, error_type) group instead of one log per RPC call,
 * keeping downstream log ingestion bounded while still giving request volume,
 * latency and error breakdowns. The logger decides whether/where to forward it
 * for the current runtime (BetterStack in prod, no-op when forwarding is off).
 * Each event carries the browser `sessionId` (log context) and `tab_id`, so a
 * failure loop in one tab can be told apart from a chain-wide outage.
 */
import { getClientTabId } from '@/lib/observability/client/session'
import { getClientSessionLogger, type LoggerEvent } from '@/lib/observability/logger'

import { aggregateRpcSamples, type RpcCallSample } from './aggregate'

export const RPC_USAGE_FLUSH_WINDOW_SECONDS = 10

/**
 * @param windowSeconds Actual elapsed span the samples cover, so a consumer
 * turning `count` into a rate stays accurate for early flushes (buffer full,
 * tab hidden, pagehide) that cover much less than the configured interval.
 * Defaults to the configured interval.
 */
export function emitRpcUsageEvent(
  samples: readonly RpcCallSample[],
  windowSeconds: number = RPC_USAGE_FLUSH_WINDOW_SECONDS
): void {
  if (samples.length === 0) return

  try {
    const logger = getClientSessionLogger()
    const tabId = getClientTabId()

    for (const bucket of aggregateRpcSamples(samples)) {
      const event: LoggerEvent = {
        event: 'rpc_usage',
        chain_id: bucket.chain_id,
        method: bucket.method,
        status: bucket.status,
        ...(bucket.error_type ? { error_type: bucket.error_type } : {}),
        ...(bucket.error_message ? { error_message: bucket.error_message } : {}),
        count: bucket.count,
        total_duration_s: Number(bucket.total_duration_s.toFixed(3)),
        max_duration_s: Number(bucket.max_duration_s.toFixed(3)),
        window_s: Number(windowSeconds.toFixed(3)),
        ...(tabId ? { tab_id: tabId } : {})
      }

      logger.info(event)
    }
  } catch {
    // Telemetry must never break the app.
  }
}
